import { Component, Vue, Provide, Watch } from 'vue-property-decorator';
import {
    ApiErrorResponse,
    createDtoCustomer,
    CustomerFieldConfig,
    createCustomerFieldConfig,
    DtoMemberType,
    createDtoMemberType,
    CustomerType,
    DtoMemberField,
    DtoUserAccount,
    createDtoUserAccount,
    DtoAddress
} from '@/common/types/AllTypesExports';
import ProfileEdit from '../../components/membership/ProfileEdit.vue';
import { IMemberRegistrationGlobals } from './MemberRegistrationGlobals';
import { customerRepo } from '../../common/repositories/CustomerRepo';
import { AxiosResponse } from 'axios';
import NoviLogging from '../../common/services/NoviLogging';
import NoviStepper from '../../components/stepper/NoviStepper.vue'
import Step, { createStep } from '../../components/stepper/Step';
import ErrorAlert from '../../components/errors/ErrorAlert.vue';
import ValidationErrors from '../../components/errors/ValidationErrors.vue';
import AccountInfo from '../create-account/AccountInfo.vue';
import { DtoCustomer } from '../../common/types/membership/DtoCustomer';
import MembershipCheckout from '../membership-renewal/MembershipCheckout.vue'
import MemberTypeSelection from '../membership-renewal/MemberTypeSelection.vue';
import UrlHelpers from '@/common/services/UrlHelpers';
import SecretLinkStorage from '@/common/services/SecretLinkStorage';
import { DuplicateCheckResult } from '../../common/types/AllTypesExports';
import MemberRegistrationDupeResult from './MemberRegistrationDupeResult.vue';
import DuplicateChecker from '../../pages/create-account/DuplicateChecker.vue';
import { default as DuplicateCheckerClass } from '@/pages/create-account/DuplicateCheckerVue';
import ValidationService from '../../common/services/ValidationService';
import { default as ProfileEditClass } from '@/components/membership/ProfileEditVue';
import RecaptchaInput from '@/components/inputs/recaptcha/RecaptchaInput.vue';
import RecaptchaInputClass from '../../components/inputs/recaptcha/RecaptchaInputVue';
import { DtoCustomerCreateResponse } from '../../common/types/membership/DtoCustomerCreateResponse';
import EventBus from '../../common/services/EventBus';
import { fillRecaptchaToken } from '../../common/types/recaptcha/RecaptchaEnterprise';

declare let noviNs: IMemberRegistrationGlobals;

@Component({
    name: 'MembershipRegistration',
    components: {
        MemberTypeSelection,
        ProfileEdit,
        NoviStepper,
        ErrorAlert,
        ValidationErrors,
        AccountInfo,
        MembershipCheckout,
        MemberRegistrationDupeResult,
        DuplicateChecker,
        RecaptchaInput
    }
})
export default class MembershipRegistration extends Vue {
    userAccount: DtoUserAccount = createDtoUserAccount();
    customer: DtoCustomer = createDtoCustomer();
    parentCustomer: DtoCustomer = createDtoCustomer();
    memberType: DtoMemberType = createDtoMemberType();

    customerFieldConfig: CustomerFieldConfig = createCustomerFieldConfig();
    companyFieldConfig: CustomerFieldConfig = createCustomerFieldConfig();

    errorMessage: string = '';
    hasErrorOnSave: boolean = false;
    isSaving: boolean = false;
    steps: Array<Step> = [];
    currentStep: Step = createStep();
    emailIsAllowed: boolean = false;
    parentCustomerDupeCheckResult: DuplicateCheckResult | null = null;
    parentCustomerDupeCheckLocation: string = "afterName";
    customerDupeCheckResult: DuplicateCheckResult | null = null;
    customerDupeCheckLocation: string = "afterName";
    recaptchaToken: string = '';
    usingExistingParentCustomer = false;
    /** Join company path: hide company detail fields until no-match or Create new. */
    showCompanyDetailFields = false;
    isSearchingParentCompany = false;
    /** Ignores stale findDuplicateCompany responses when a newer search started. */
    companyDupeCheckSeq = 0;
    /** Set after create-new-member succeeds; locks earlier steps and skips re-create. */
    newMemberCreatedInWizard = false;
    memberVerificationCode = '';
    userLoggedInOnMemberRegistration: boolean = false;
    secretLink: boolean = false;
    isLoadingFieldConfig: boolean = false;
    fieldConfigLoadFailed: boolean = false;
    fieldConfigLoaded: boolean = false;


    @Provide('validator') $validator: any = this.$validator;

    @Provide('userLoggedInOnMemberRegistration')
    get loginStateProvider(): () => boolean {
        return () => this.userLoggedInOnMemberRegistration;
    }

    /// Computed Properties
    get customerFields() {
        const result: { [key: string]: DtoMemberField } = {};
        this.customerFieldConfig.memberFields.forEach(memberField => {
            if (!memberField) {
                return;
            }
            result[memberField.name.toLowerCase()] = memberField;
        });
        return result;
    }

    get companyFields() {
        const result: { [key: string]: DtoMemberField } = {};
        this.companyFieldConfig.memberFields.forEach(memberField => {
            if (!memberField) {
                return;
            }
            result[memberField.name.toLowerCase()] = memberField;
        });
        return result;
    }

    get parentMemberLabelForPeople(): string { return noviNs.parentMemberLabelForPeople };
    get parentMemberDescriptionForPeople(): string { return noviNs.parentMemberDescriptionForPeople };
    get parentMemberLabelForCompanies(): string { return noviNs.parentMemberLabelForCompanies };
    get parentMemberDescriptionForCompanies(): string { return noviNs.parentMemberDescriptionForCompanies };

    get billingAddressInstructionsForPeople(): string { return noviNs.billingAddressInstructionsForPeople };
    get shippingAddressInstructionsForPeople(): string { return noviNs.shippingAddressInstructionsForPeople };
    get billingAddressInstructionsForCompanies(): string { return noviNs.billingAddressInstructionsForCompanies };
    get shippingAddressInstructionsForCompanies(): string { return noviNs.shippingAddressInstructionsForCompanies };
    get personalAddressInstructions(): string { return noviNs.personalAddressInstructions };
    get professionalEmailInstructions(): string { return noviNs.professionalEmailInstructions };
    get personalEmailInstructions(): string { return noviNs.personalEmailInstructions };
    get emailInstructionsForCompanies(): string { return noviNs.emailInstructionsForCompanies };
    get reCaptchaUpgradeEnabled(): boolean { return !!noviNs.reCaptchaUpgradeEnabled; }

    get nextStepText() {
        if (this.currentStep == this.keyInfoStep) {
            if (this.usingExistingParentCustomer) {
                return 'Create Account';
            }
            else return 'Save & Continue';
        }

        return 'Continue';
    }

    get matchedCompanyHelperText() {
        if (this.parentCustomerDupeCheckResult?.isCurrentMember) {
            return "We've matched your company to an existing record. Finish creating your account to get started.";
        }

        return "We've matched your company to an existing record. After you create your account and verify your email, you can complete your company's application from your Member Compass. If you need assistance, contact us.";
    }

    get showNext() {
        if (this.currentStep == this.checkoutStep)
        return false;

        return true;
    }

    get disableNext() {
        const fieldConfigBlocking =
            (this.currentStep == this.keyInfoStep) && (this.isLoadingFieldConfig || this.fieldConfigLoadFailed || !this.fieldConfigLoaded);

        if (this.currentStep == this.loginInfoStep && !this.emailIsAllowed)
            return true;

        const companySearchBlocking =
            this.currentStep == this.keyInfoStep
            && this.memberType.forCompanies
            && !this.usingExistingParentCustomer
            && (this.isSearchingParentCompany || !this.showCompanyDetailFields);

        return fieldConfigBlocking || companySearchBlocking || (this.errors && this.errors.count() > 0) || this.isSaving;
    }

    get duesPayer() {
        if (this.memberType.forCompanies)
            return this.parentCustomer;
        else
            return this.customer;
    }

    /** After create-new-member, keep earlier steps locked even if bounced off Checkout. */
    get lockStepsAtCheckout(): boolean {
        return this.newMemberCreatedInWizard;
    }

    get showParentMemberFieldInKeyInfoStep() {
        var parentMemberField = this.customerFieldConfig.memberFields.find(mf => mf.name.toLowerCase() == "parentcustomername");
        if (parentMemberField) {
            return parentMemberField.keyInformation;
        }
        return false;
    }

    private memberTypeStep: Step = {
        name: "membertype",
        title: "Member Type",
        iconClass: "fas fa-handshake",
        completed: false
    };
    private loginInfoStep: Step = {
        name: "login-info",
        title: "Login Info",
        iconClass: "fas fa-user",
        completed: false
    };
    private keyInfoStep: Step = {
        name: "key-info",
        title: "Key Info",
        iconClass: "fas fa-key",
        completed: false
    };
    private checkoutStep: Step = {
        name: "checkout",
        title: "Checkout",
        iconClass: "fas fa-shopping-cart",
        completed: false
    };

    ///Life cycle events

    created() {
        this.customer.customerType = CustomerType.Person;
        this.parentCustomer.customerType = CustomerType.Company;
        
        var memberTypeId = this.getQueryStringMemberTypeId();
        if (memberTypeId) {
            this.loadSingleMemberType(memberTypeId);
            this.currentStep = this.loginInfoStep;
            this.memberTypeStep.completed = true;
        }
        else {
            this.currentStep = this.memberTypeStep;
        }

        this.steps.push(this.memberTypeStep);
        this.steps.push(this.loginInfoStep);
        this.steps.push(this.keyInfoStep);

        EventBus.$on("file-uploaded", this.clearFileUploadErrors);
    }

    //Public Functions
    onUserAccountUpdated() {
        this.customer.firstName = this.userAccount.firstName;
        this.customer.lastName = this.userAccount.lastName;
        this.customer.name = `${this.userAccount.firstName} ${this.userAccount.lastName}`;
        this.customer.email = this.userAccount.email;
    }

    onUserAccountEmailUpdated() {
        this.checkForDuplicateEmail();
    }

    onCustomerParentSelectionUpdated() {
        if (!this.customer.parentCustomerId && this.customer.parentCustomerName)
            this.parentCustomer.name = this.customer.parentCustomerName;
        this.checkForDuplicateCustomer("afterName");
    }

    onParentCustomerBillingAddressUpdated() {
        Object.assign(<DtoAddress>this.customer.parentBilling, this.parentCustomer.billingAddress);
        if (this.customer.useParentBilling) {
            Object.assign(<DtoAddress>this.customer.billingAddress, this.parentCustomer.billingAddress);
        }
        this.checkForDuplicateParentCustomer("afterBillingAddress");
    }
    onParentCustomerShippingAddressUpdated() {
        Object.assign(<DtoAddress>this.customer.parentShipping, this.parentCustomer.shippingAddress);
        if (this.customer.useParentShipping) {
            Object.assign(<DtoAddress>this.customer.shippingAddress, this.parentCustomer.shippingAddress);
        }
        this.checkForDuplicateParentCustomer("afterShippingAddress");
    }
    onParentCustomerNameUpdated() {
        this.checkForDuplicateParentCustomer("afterName");
    }
    onCustomerNameUpdated() {
        this.checkForDuplicateCustomer("afterName");
    }
    onCustomerEmailUpdated() {
        this.checkForDuplicateCustomer("afterEmail");
    }

    checkForDuplicateEmail() {
        let duplicateChecker = <DuplicateCheckerClass>this.$refs.duplicateEmailChecker;
        duplicateChecker.validateEmailAddress()
            //.then((result: void | DuplicateCheckResult | AxiosResponse<ApiErrorResponse>) => { })
            .catch((error: AxiosResponse<ApiErrorResponse>) => { });
    }

    checkForDuplicateParentCustomer(dupeCheckLocation: string) {
        if (this.usingExistingParentCustomer) {
            return;
        }

        if (!this.parentCustomer.name || !this.parentCustomer.name.trim()) {
            this.companyDupeCheckSeq++;
            this.parentCustomerDupeCheckResult = null;
            this.showCompanyDetailFields = false;
            this.isSearchingParentCompany = false;
            return;
        }

        const seq = ++this.companyDupeCheckSeq;
        this.isSearchingParentCompany = true;
        customerRepo.findDuplicateCompany(
            this.parentCustomer.name,
            this.parentCustomer.billingAddress,
            this.parentCustomer.shippingAddress)
        .then((result : DuplicateCheckResult) => {
            // Seq bump or Continue-with-match can land while this request was in flight.
            if (seq !== this.companyDupeCheckSeq || this.usingExistingParentCustomer) {
                return;
            }

            if (result.hasDuplicate) {
                const sameName = this.parentCustomerDupeCheckResult
                    && this.parentCustomerDupeCheckResult.name == result.name;
                const wasCreatingNew = !!(sameName && this.parentCustomerDupeCheckResult?.ignored);
                const nameDrivenSearch = dupeCheckLocation === "afterName";

                // Address blur while already on the match card: nothing to update.
                if (sameName && !wasCreatingNew && !nameDrivenSearch) {
                    return;
                }

                // Address blur while on Create-new: don't re-open the match card mid-edit.
                if (wasCreatingNew && !nameDrivenSearch) {
                    return;
                }

                // Name search (incl. after no-match or accidental Create-new) always re-opens match UI.
                this.parentCustomerDupeCheckLocation = dupeCheckLocation;
                result.ignored = false;
                this.showCompanyDetailFields = false;
            }
            else {
                this.showCompanyDetailFields = true;
            }

            this.parentCustomerDupeCheckResult = result;
        })
        .catch(() => {
            if (seq !== this.companyDupeCheckSeq || this.usingExistingParentCustomer) {
                return;
            }

            // Allow Create-new after timeout/500 so Next is not stuck disabled.
            this.parentCustomerDupeCheckResult = null;
            this.showCompanyDetailFields = true;
        })
        .finally(() => {
            if (seq !== this.companyDupeCheckSeq) {
                return;
            }

            this.isSearchingParentCompany = false;
        });
    }

    checkForDuplicateCustomer(dupeCheckLocation: string) {
        if (!this.customer.email) {
            this.customerDupeCheckResult = null;
            return;
        }
        this.customerDupeCheckLocation = dupeCheckLocation;
        customerRepo.checkForDuplicate(this.customer.email, this.customer.firstName, this.customer.lastName, this.customer.parentCustomerId)
            .then((result: DuplicateCheckResult) => {
                if (result.hasDuplicate) {
                    if (this.customerDupeCheckResult && this.customerDupeCheckResult.name == result.name)
                        return;

                    this.customerDupeCheckLocation = dupeCheckLocation;
                    result.ignored = false;                    
                }

                this.customerDupeCheckResult = result;
            });
    }

    validateProfiles() {
        let personProfile = <ProfileEditClass>this.$refs.personProfile;
        let companyProfile = <ProfileEditClass>this.$refs.companyProfile;
        let newParentProfile = <ProfileEditClass>this.$refs.newParentProfile;

        if (personProfile) personProfile.validate();
        if (companyProfile) companyProfile.validate();
        if (newParentProfile) newParentProfile.validate();
    }

    saveAndNext() {
        // Ignore a second Continue while validate/create is already running.
        if (this.isSaving) {
            return;
        }

        if (this.currentStep == this.keyInfoStep) {
            this.validateProfiles();
        }

        this.isSaving = true;
        this.$validator.validate()
            .then((valid: any) => {
                if (valid && !this.$validator.errors.any()) {
                    if (this.currentStep == this.keyInfoStep) {
                        if (this.usingExistingParentCustomer) {
                            this.saveUserAccount();
                        }
                        else {
                            this.saveKeyInfo();
                        }
                    }
                    else {
                       this.isSaving = false;
                       this.moveToNextStep();
                    }
                }
                else {
                    this.isSaving = false;
                    NoviLogging.info("Validation Failed");
                    ValidationService.scrollToFirstInvalidField(this.$el);
                }
            })
            .catch(() => {
                this.isSaving = false;
            });
    }

    setUsingExistingParentCustomer() {
        this.usingExistingParentCustomer = true;
        this.showCompanyDetailFields = false;
        // Drop any in-flight name/address dupe search (e.g. blur before this click) so it
        // cannot reopen the match card after Continue.
        this.companyDupeCheckSeq++;
        this.isSearchingParentCompany = false;
        if (this.parentCustomerDupeCheckResult) {
            this.parentCustomerDupeCheckResult.ignored = true;
        }
        if (this.parentCustomerDupeCheckResult?.customerId) {
            this.customer.parentCustomerId = this.parentCustomerDupeCheckResult.customerId;
            // Keep memberType on the rebuilt parent DTO — create-new-member requires it on
            // Customer or ParentCustomer, and Create-new after Continue reuses this object.
            this.parentCustomer = createDtoCustomer({
                name: this.parentCustomerDupeCheckResult?.name,
                id: this.parentCustomerDupeCheckResult?.customerId,
                billingAddress: this.parentCustomerDupeCheckResult?.addressObject,
                memberType: this.memberType
            });
        }
    }

    onCreateNewParentCompany() {
        this.usingExistingParentCustomer = false;
        this.showCompanyDetailFields = true;
        this.customer.parentCustomerId = null;
        this.parentCustomer.id = 0;
        if (this.memberType.forCompanies) {
            this.parentCustomer.memberType = this.memberType;
            this.customer.memberType = null;
        }
    }

    //Private Functions
    private moveToNextStep() {
        this.currentStep.completed = true;

        if (this.currentStep == this.keyInfoStep && this.memberType.requireApplication) {
            window.location.href = `/confirm-email?userId=${this.duesPayer.userId}&checkoutType=new&memberTypeId=${this.memberType.id}`;
        }
        else {
            var nextStep = this.steps[this.steps.indexOf(this.currentStep) + 1];
            this.currentStep = nextStep;
            window.scrollTo({ top: (<HTMLElement>this.$refs.container).offsetTop });
        }
    }

    private saveKeyInfo() {
        // Account already created — do not POST create-new-member again (e.g. validation
        // bounce back to Key Info). Edits after create are not supported in this wizard.
        if (this.newMemberCreatedInWizard) {
            this.isSaving = false;
            this.moveToNextStep();
            return;
        }

        this.isSaving = true;
        this.ensureRecaptchaTokenForSubmit(noviNs.recaptchaEnterpriseActionCreateNewMember)
            .then(() => {
                var parentCustomer = this.memberType.forCompanies || this.customer.parentCustomerId == 0 ? this.parentCustomer : undefined;
                return customerRepo.createNewMember(this.customer, this.userAccount, this.secretLink, parentCustomer, this.recaptchaToken);
            })
            .then((savedCustomer) => {
                this.duesPayer.id = savedCustomer.id;
                this.duesPayer.userId = savedCustomer.userId;
                this.duesPayer.defaultBillablePartyId = savedCustomer.defaultBillablePartyId;
                this.newMemberCreatedInWizard = true;
                this.moveToNextStep();
                this.isSaving = false;
                this.hasErrorOnSave = false;
                this.userLoggedInOnMemberRegistration = true;
            })
            .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => {
                this.handleSaveUserAccountError(axiosResponse);
            });
    }

    private saveUserAccount() {
        this.isSaving = true;

        if (this.parentCustomerDupeCheckResult?.customerId) {
            this.customer.parentCustomerId = this.parentCustomerDupeCheckResult.customerId;
        }

        this.ensureRecaptchaTokenForSubmit(noviNs.recaptchaEnterpriseActionCreateUserAccount)
            .then(() => customerRepo.createUserAccount(this.customer, this.userAccount, this.memberVerificationCode, this.parentCustomer, this.recaptchaToken))
            .then((result: DtoCustomerCreateResponse) => {
                if (result.isAutoLoggedIn) {
                    window.location.href = `/member-compass`;
                } else {
                    window.location.href = `/confirm-email?userId=` + result.userId;
                }
            })
            .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => {
                this.handleSaveUserAccountError(axiosResponse);
            });
    }

    private handleSaveUserAccountError(axiosResponse: AxiosResponse<ApiErrorResponse>) {
        this.isSaving = false;
        this.keyInfoStep.completed = false;
        var customerType = this.customer.customerType || CustomerType.Person;
        this.errors.clear(customerType);

        let validationErrors = axiosResponse?.data?.links || {};
        const hasValidationErrors = Object.keys(validationErrors).length > 0;

        let recaptchaInput = <RecaptchaInputClass>this.$refs.recaptcha;
        if (!this.reCaptchaUpgradeEnabled && recaptchaInput) {
            recaptchaInput.reset();
        }

        if (!hasValidationErrors) {
            this.errorMessage = `Could not create your account: ${axiosResponse?.data?.message || 'Unknown error'}`;
            this.hasErrorOnSave = true;
            return;
        }

        this.errorMessage = "";
        this.hasErrorOnSave = false;

        /*
         *  The validation errors returned contains errors for both the individual and the company
         *  We need to separate them so that we can add the errors to the appropriate scope
         */

        // Individual error keys look like "request.customer.phone"
        var individualValidationErrors = Object.keys(validationErrors)
            .filter(error => !error.includes("parent"))
            .reduce((obj: any, key) => {
                obj[key] = validationErrors[key];
                return obj;
            }, {});

        // Company error keys look like "request.parentcustomer.phone"
        var companyValidationErrors = Object.keys(validationErrors)
            .filter(error => error.includes("parent"))
            .reduce((obj: any, key) => {
                obj[key] = validationErrors[key];
                return obj;
            }, {});
        
        ValidationService.parseValidationErrors(individualValidationErrors, CustomerType.Person, this.customerFields, this.$validator);
        ValidationService.parseValidationErrors(companyValidationErrors, CustomerType.Company, this.companyFields, this.$validator);
        ValidationService.scrollToFirstInvalidField(this.$el);
    }

    @Watch("memberType")
    onMemberTypeChanged() {
        if (this.memberType.forCompanies) {
            this.parentCustomer.memberType = this.memberType;
            this.customer.memberType = null;
        }
        else {
            this.customer.memberType = this.memberType;
            this.parentCustomer.memberType = null;
        }

        this.usingExistingParentCustomer = false;
        this.showCompanyDetailFields = false;
        this.isSearchingParentCompany = false;
        this.parentCustomerDupeCheckResult = null;

        this.keyInfoStep.completed = false;
        this.checkoutStep.completed = false;

        if (this.currentStep === this.checkoutStep) {
            this.currentStep = this.keyInfoStep;
        }

        this.getFieldConfig();
    }

    @Watch("currentStep")
    async onCurrentStepUpdated(newStep: Step) {
        if (newStep !== this.checkoutStep) return;

        this.validateProfiles();

        const valid = await this.$validator.validate();
        if (!valid || this.$validator.errors.any()) {
            this.checkoutStep.completed = false;
            this.currentStep = this.keyInfoStep;
            ValidationService.scrollToFirstInvalidField(this.$el);
            return;
        }

        if (!this.duesPayer.id) {
            this.checkoutStep.completed = false;
            this.currentStep = this.keyInfoStep;
        }
    }

    onProfileEditMounted() {
        var firstInput = this.$el.querySelector("input");
        if (firstInput) {
            (<HTMLElement>firstInput).focus();
        }

        if (!this.showParentMemberFieldInKeyInfoStep) {
            this.checkForDuplicateCustomer("afterName");
        }
    }

    clearFileUploadErrors() {
        this.errors.clear(CustomerType.Person);
    }

    private sleep(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private async ensureRecaptchaTokenForSubmit(recaptchaAction: string): Promise<void> {
        if (!this.reCaptchaUpgradeEnabled) {
            return;
        }

        const input = document.querySelector<HTMLInputElement>('input[name="g-recaptcha-response"]');
        const siteKey = (window as any).__recaptchaEnterpriseSiteKey as string | undefined;

        if (!input || !siteKey) {
            this.recaptchaToken = '';
            return;
        }

        this.recaptchaToken = await new Promise<string>((resolve) => {
            fillRecaptchaToken(siteKey, (token) => {
                input.value = token || '';
                resolve(token || '');
            }, recaptchaAction);
        });
    }

    private async retry<T>(fn: () => Promise<T>, attempts = 3, delayMs = 800): Promise<T> {
        let lastErr: any;
        for (let i = 0; i < attempts; i++) {
            try {
                return await fn();
            } catch (e) {
                lastErr = e;
                if (i < attempts - 1) {
                    await this.sleep(delayMs);
                    delayMs = Math.min(delayMs * 2, 4000);
                }
            }
        }
        throw lastErr;
    }

    private async getFieldConfig() {
        this.isLoadingFieldConfig = true;
        this.fieldConfigLoadFailed = false;
        this.fieldConfigLoaded = false;
        this.errorMessage = "";

        try {
            const [personCfg, companyCfg] = await Promise.all([
                this.retry(() => customerRepo.getFieldConfiguration(false, true, this.memberType.id), 3, 800),
                this.retry(() => customerRepo.getFieldConfiguration(true, true, this.memberType.id), 3, 800),
            ]);

            this.customerFieldConfig = Object.assign(this.customerFieldConfig, personCfg);
            this.companyFieldConfig = Object.assign(this.companyFieldConfig, companyCfg);
            this.fieldConfigLoaded = true;
        } catch (axiosResponse) {
            const ar = axiosResponse as any;
            const msg = ar?.data?.message || "Unknown error";
            this.errorMessage = `Unable to load the field configuration information. ${msg}`;
            this.fieldConfigLoadFailed = true;
            throw axiosResponse;
        } finally {
            this.isLoadingFieldConfig = false;
        }
    }

    private getQueryStringMemberTypeId() : number | null {
        let query = UrlHelpers.getUrlParameter('memberTypeId');
        if (!query) {
            return null;
        }

        return parseInt(query);
    }

    loadSingleMemberType(memberTypeId: number) {
        const isSecretLinkFromUrl = UrlHelpers.getUrlParameter('isSecretLink').toLowerCase() === 'true';
        this.secretLink = SecretLinkStorage.resolve(memberTypeId, isSecretLinkFromUrl);

        if (this.secretLink) {
            customerRepo.getSecretMemberTypes(memberTypeId)
                .then(result => {
                    this.memberType = result[0];
                    if (this.memberType && !this.memberType.requireApplication) {
                        this.steps.push(this.checkoutStep);
                    }
                })
                .catch((message) => {
                    this.errorMessage = message;
                });
            //.finally(() => this.isLoading = false);
        }
        else {
            customerRepo.getMemberTypes(memberTypeId)
                .then(result => {
                    this.memberType = result[0];
                    if (this.memberType && !this.memberType.requireApplication) {
                        this.steps.push(this.checkoutStep);
                    }
                })
                .catch((message) => {
                    this.errorMessage = message;
                });
                //.finally(() => this.isLoading = false);
        }
    }
}